import { useEffect, useState } from "react";
import { ShieldCheck, ChevronDown, ChevronUp, CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * ThreeQCoverageBadge — Tracker Doctrine 3Q.
 *
 * V23.15.37 — Rend visible la couverture des 3 questions sur les modules.
 * Compact par défaut, avec panneau déroulant montrant les modules validés
 * et ceux encore à traiter.
 */
export default function ThreeQCoverageBadge() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/three-q/coverage");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const { validated_count, total, percent, modules_validated, modules_pending, doctrine_version } = data;
  const color = percent >= 60 ? "#1D9E75" : percent >= 30 ? "#EF9F27" : "#7F77DD";

  return (
    <div
      className="noam-card overflow-hidden"
      data-testid="three-q-coverage-badge"
      style={{ borderColor: `${color}55` }}
    >
      {/* Compact header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors text-left"
        data-testid="three-q-coverage-toggle"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}
        >
          <ShieldCheck className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
            Doctrine 3Q · Couverture · {doctrine_version}
          </div>
          <div className="text-sm text-zinc-100 mt-0.5 flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold" data-testid="three-q-coverage-count">
              {validated_count}/{total} modules validés
            </span>
            <span className="text-xs text-zinc-500">
              — chaque écran répond « Et alors ? · Pourquoi maintenant ? · Qu&apos;est-ce que je fais ? »
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 mt-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${percent}%`, background: color }}
              data-testid="three-q-coverage-bar"
            />
          </div>
        </div>
        <div
          className="text-2xl font-bold shrink-0 tabular-nums"
          style={{ color }}
          data-testid="three-q-coverage-percent"
        >
          {percent}%
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-white/5 grid md:grid-cols-2 gap-0" data-testid="three-q-coverage-detail">
          <div className="p-4 border-r-0 md:border-r border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Validés ({modules_validated.length})
            </div>
            <div className="space-y-1">
              {modules_validated.map((m) => (
                <Link
                  key={m.key}
                  to={m.route}
                  className="flex items-center gap-2 text-xs text-zinc-300 hover:text-white px-2 py-1 rounded hover:bg-white/[0.03]"
                  data-testid={`three-q-validated-${m.key}`}
                >
                  <CheckCircle2 className="w-3 h-3 text-[#1D9E75] shrink-0" />
                  <span className="flex-1 truncate">{m.label}</span>
                  <ExternalLink className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </div>
          <div className="p-4 border-t md:border-t-0 border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 flex items-center gap-1.5">
              <Circle className="w-3 h-3" /> À traiter ({modules_pending.length})
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {modules_pending.map((m) => (
                <Link
                  key={m.key}
                  to={m.route}
                  className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white px-2 py-1 rounded hover:bg-white/[0.03]"
                  data-testid={`three-q-pending-${m.key}`}
                >
                  <Circle className="w-3 h-3 text-zinc-600 shrink-0" />
                  <span className="flex-1 truncate">{m.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
